/**
 * Pricing Page Logic
 * Handles tier display, billing toggle, and subscription management
 */

// Initialize Sentry as early as possible
import * as Sentry from "@sentry/browser";

Sentry.init({
  dsn: "https://98f444c73599f68edcf65d9db57c665f@o4510773732245504.ingest.us.sentry.io/4510774427189248",
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({ levels: ["warn", "error"] }),
  ],
  _experiments: {
    enableLogs: true,
  },
  tracesSampleRate: 1.0,
  tracePropagationTargets: ["localhost", /^https:\/\/.*\.xrgallery\.io/],
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

import { onAuthChange, getCurrentUser } from '../src/firebase/auth';
import {
  ensureUserProfile,
  TIER_CONFIG,
  FEATURE_DISPLAY_NAMES,
  type UserTier,
  type UserProfile,
  type BillingCycle,
  type FeatureKey,
  canUpgradeTo,
  formatMuxMinutes,
} from '../src/api/UserProfileAPI';
import { isFeatureEnabled } from '../src/config/featureFlags';
import { initToolbar, updateToolbarProfile, showUsageModal, showSettingsModal } from '../src/ui/UserDropdown';
import { logOut } from '../src/firebase/auth';
import { functions } from '../src/firebase/config';
import { httpsCallable } from 'firebase/functions';
import { STRIPE_PUBLISHABLE_KEY, getPriceId, isStripeConfigured, type StripeTier } from '../src/config/stripe';
import { showAlert } from '../src/ui/ModalManager';

// Check feature flag - redirect if pricing page is disabled
if (!isFeatureEnabled('PRICING_PAGE')) {
  window.location.href = '/dashboard/';
}

// DOM Elements
let pricingGrid: HTMLElement | null;
let comparisonTable: HTMLElement | null;
let currentPlanBanner: HTMLElement | null;
let currentPlanName: HTMLElement | null;
let planStatus: HTMLElement | null;
let loadingOverlay: HTMLElement | null;
let userEmailEl: HTMLElement | null;
let upgradeBtn: HTMLButtonElement | null;
let proBadge: HTMLElement | null;
// Note: usageBtn, settingsBtn, signOutBtn, userDropdown, userDropdownToggle are now handled by shared initToolbar

// State
let currentUser: { uid: string; email: string | null } | null = null;
let userProfile: UserProfile | null = null;
let billingCycle: BillingCycle = 'monthly';

// Tiers to display (exclude admin)
const DISPLAY_TIERS: UserTier[] = ['free', 'basic', 'pro', 'enterprise'];

// All features for comparison table
const ALL_FEATURES: { key: FeatureKey | 'storage' | 'bandwidth' | 'mux_video_minutes' | 'ai_quota'; name: string }[] = [
  { key: 'storage', name: 'Cloud Storage' },
  { key: 'bandwidth', name: 'Monthly Bandwidth' },
  { key: 'mux_video_minutes', name: 'Video Upload Minutes' },
  { key: 'ai_quota', name: 'AI 360° Generation' },
  { key: 'uploads', name: 'Media Uploads' },
  { key: 'lead_capture', name: 'Lead Capture Forms' },
  { key: 'analytics', name: 'Basic Analytics' },
  { key: 'full_analytics', name: 'Advanced Analytics' },
  { key: 'white_label', name: 'White Label Branding' },
  { key: 'priority_support', name: 'Priority Support' },
  { key: 'sla', name: 'SLA Guarantee' },
  { key: 'self_host_export', name: 'Self-Host Export' },
];

/**
 * Initialize the pricing page
 */
async function init() {
  // Get DOM elements
  pricingGrid = document.getElementById('pricing-grid');
  comparisonTable = document.getElementById('comparison-table');
  currentPlanBanner = document.getElementById('current-plan-banner');
  currentPlanName = document.getElementById('current-plan-name');
  planStatus = document.getElementById('plan-status');
  loadingOverlay = document.getElementById('loading-overlay');
  userEmailEl = document.getElementById('user-email');
  upgradeBtn = document.getElementById('upgrade-btn') as HTMLButtonElement | null;
  proBadge = document.getElementById('pro-badge') as HTMLElement | null;

  // Setup billing toggle
  setupBillingToggle();

  // Setup mobile menu handlers
  setupMobileMenu();

  // Initialize shared toolbar (will be updated after auth)
  // Note: initToolbar is called in onAuthChange after profile is loaded

  // Listen for auth changes
  onAuthChange(async (user) => {
    if (user) {
      currentUser = { uid: user.uid, email: user.email };
      userProfile = await ensureUserProfile(user.uid, user.email || undefined);

      if (userEmailEl) {
        userEmailEl.textContent = user.email || 'User';
      }

      // Set mobile menu email
      const mobileUserEmail = document.getElementById('mobile-user-email');
      if (mobileUserEmail) {
        mobileUserEmail.textContent = user.email || 'User';
      }

      // Initialize shared toolbar (dropdown, usage, settings, signout, tier badge)
      initToolbar({
        userProfile,
        userId: user.uid,
      });

      // Show/hide admin dashboard button based on tier
      const adminDashboardBtn = document.getElementById('admin-dashboard-btn');
      if (adminDashboardBtn) {
        adminDashboardBtn.style.display = userProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide newsletter admin button based on tier
      const newsletterAdminBtn = document.getElementById('newsletter-admin-btn');
      if (newsletterAdminBtn) {
        newsletterAdminBtn.style.display = userProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide blog admin button based on tier
      const blogAdminBtn = document.getElementById('blog-admin-btn');
      if (blogAdminBtn) {
        blogAdminBtn.style.display = userProfile.tier === 'admin' ? 'flex' : 'none';
      }

      // Show/hide admin items in mobile menu
      document.querySelectorAll('.admin-mobile-btn').forEach((el) => {
        (el as HTMLElement).style.display = userProfile.tier === 'admin' ? 'flex' : 'none';
      });

      // Show/hide admin items in dropdown menu
      document.querySelectorAll('.admin-dropdown-btn').forEach((el) => {
        (el as HTMLElement).style.display = userProfile.tier === 'admin' ? 'flex' : 'none';
      });

      // Show current plan banner
      if (currentPlanBanner && currentPlanName) {
        currentPlanBanner.style.display = 'block';
        const tierConfig = TIER_CONFIG[userProfile.tier];
        currentPlanName.textContent = tierConfig.displayName;

        if (planStatus && userProfile.subscriptionStatus === 'active') {
          planStatus.textContent = '(Active subscription)';
        }
      }
    } else {
      currentUser = null;
      userProfile = null;
      if (userEmailEl) {
        userEmailEl.textContent = 'Sign In';
        userEmailEl.style.cursor = 'pointer';
        userEmailEl.onclick = () => window.location.href = '/dashboard/auth.html';
      }
      if (currentPlanBanner) {
        currentPlanBanner.style.display = 'none';
      }
    }

    // Render pricing cards and comparison table
    renderPricingCards();
    renderComparisonTable();

    // Hide loading overlay
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
    }
  });
}

/**
 * Setup billing cycle toggle
 */
function setupBillingToggle() {
  const monthlyBtn = document.getElementById('toggle-monthly');
  const yearlyBtn = document.getElementById('toggle-yearly');

  monthlyBtn?.addEventListener('click', () => {
    billingCycle = 'monthly';
    monthlyBtn.classList.add('active');
    yearlyBtn?.classList.remove('active');
    renderPricingCards();
  });

  yearlyBtn?.addEventListener('click', () => {
    billingCycle = 'yearly';
    yearlyBtn.classList.add('active');
    monthlyBtn?.classList.remove('active');
    renderPricingCards();
  });
}

/**
 * Setup mobile menu handlers
 */
function setupMobileMenu() {
  const mobileUsageBtn = document.getElementById('mobile-usage-btn');
  const mobileSettingsBtn = document.getElementById('mobile-settings-btn');
  const mobileLogoutBtn = document.getElementById('mobile-logout-btn');

  function closeMobileMenu() {
    const mobileMenu = document.getElementById('mobile-menu');
    const mobileMenuOverlay = document.getElementById('mobile-menu-overlay');
    if (mobileMenu) mobileMenu.classList.remove('active');
    if (mobileMenuOverlay) mobileMenuOverlay.classList.remove('active');
    document.body.style.overflow = '';
  }

  if (mobileUsageBtn) {
    mobileUsageBtn.addEventListener('click', () => {
      closeMobileMenu();
      if (userProfile) {
        showUsageModal(userProfile);
      }
    });
  }

  if (mobileSettingsBtn) {
    mobileSettingsBtn.addEventListener('click', () => {
      closeMobileMenu();
      if (userProfile && currentUser) {
        showSettingsModal(userProfile, currentUser.uid);
      }
    });
  }

  if (mobileLogoutBtn) {
    mobileLogoutBtn.addEventListener('click', async () => {
      closeMobileMenu();
      try {
        await logOut();
        window.location.href = '/';
      } catch (error) {
        console.error('[Pricing] Error signing out:', error);
      }
    });
  }
}

// setupUserDropdown moved to shared UserDropdown.ts (initToolbar)

/**
 * Format price for display
 */
function formatPrice(tier: UserTier): { amount: string; period: string; isCustom: boolean } {
  const config = TIER_CONFIG[tier];

  if (config.price === 'custom') {
    return { amount: 'Custom', period: '', isCustom: true };
  }

  const price = billingCycle === 'yearly'
    ? Math.round(config.price.yearly / 12)
    : config.price.monthly;

  return {
    amount: price.toString(),
    period: billingCycle === 'yearly' ? '/mo (billed yearly)' : '/month',
    isCustom: false,
  };
}

/**
 * Format bytes to human readable (GB or TB)
 */
function formatBandwidth(bytes: number): string {
  if (bytes === -1) return 'Unlimited';
  if (bytes === 0) return '—';
  const gb = bytes / (1024 * 1024 * 1024);
  if (gb >= 1024) {
    return `${(gb / 1024).toFixed(0)}TB`;
  }
  return `${gb.toFixed(0)}GB`;
}

/**
 * Get feature value for a tier
 */
function getFeatureValue(tier: UserTier, featureKey: string): string | boolean {
  const config = TIER_CONFIG[tier];

  if (featureKey === 'storage') {
    if (config.storageLimit === -1) return 'Unlimited';
    if (config.storageLimit === 0) return '—';
    const gb = config.storageLimit / (1024 * 1024 * 1024);
    return `${gb}GB`;
  }

  if (featureKey === 'bandwidth') {
    return formatBandwidth(config.bandwidthLimit);
  }

  if (featureKey === 'mux_video_minutes') {
    return formatMuxMinutes(config.muxMinutesLimit);
  }

  if (featureKey === 'ai_quota') {
    if (config.aiProLimit === -1 || config.aiProLimit >= 9999) return 'Unlimited';
    if (config.aiProLimit === 0 && config.aiFlashLimit === 0) return '—';
    // Show both Pro and Flash limits
    const parts: string[] = [];
    if (config.aiProLimit > 0) parts.push(`${config.aiProLimit} Pro`);
    if (config.aiFlashLimit > 0) parts.push(`${config.aiFlashLimit} Flash`);
    return parts.join(' + ') + '/mo';
  }

  return config.features.includes(featureKey as FeatureKey);
}

/**
 * Get CTA button config for a tier
 */
function getCTAConfig(tier: UserTier): { text: string; class: string; disabled: boolean; action: () => void } {
  const isCurrentPlan = userProfile?.tier === tier;
  const canUpgrade = userProfile ? canUpgradeTo(userProfile.tier, tier) : true;

  if (isCurrentPlan) {
    return {
      text: 'Current Plan',
      class: 'current-plan',
      disabled: true,
      action: () => {},
    };
  }

  if (tier === 'free') {
    return {
      text: 'Get Started Free',
      class: 'secondary',
      disabled: false,
      action: () => {
        if (!currentUser) {
          window.location.href = '/dashboard/auth.html';
        }
      },
    };
  }

  if (tier === 'enterprise') {
    return {
      text: 'Contact Sales',
      class: 'secondary',
      disabled: false,
      action: () => {
        window.location.href = 'mailto:enterprise@xrgallery.com?subject=Enterprise%20Plan%20Inquiry';
      },
    };
  }

  if (!currentUser) {
    return {
      text: 'Sign Up',
      class: 'primary',
      disabled: false,
      action: () => {
        window.location.href = '/dashboard/auth.html?redirect=pricing&tier=' + tier;
      },
    };
  }

  if (!canUpgrade) {
    return {
      text: 'Downgrade',
      class: 'secondary',
      disabled: false,
      action: () => handlePlanChange(tier),
    };
  }

  return {
    text: 'Upgrade',
    class: 'primary',
    disabled: false,
    action: () => handlePlanChange(tier),
  };
}

/**
 * Handle plan change (upgrade/downgrade)
 */
async function handlePlanChange(targetTier: UserTier) {
  if (!currentUser) {
    window.location.href = '/dashboard/auth.html?redirect=pricing&tier=' + targetTier;
    return;
  }

  const config = TIER_CONFIG[targetTier];
  const priceInfo = formatPrice(targetTier);

  // Enterprise tier - contact sales
  if (targetTier === 'enterprise') {
    window.location.href = 'mailto:enterprise@xrgallery.com?subject=Enterprise%20Plan%20Inquiry';
    return;
  }

  // Check if Stripe payments are enabled
  if (!isFeatureEnabled('STRIPE_PAYMENTS')) {
    showAlert(`Stripe payments are not enabled yet.\n\nTo upgrade to ${config.displayName}, please contact us at support@xrgallery.com`, 'Payments Unavailable', '💳');
    return;
  }

  // Check if Stripe is configured
  if (!isStripeConfigured()) {
    console.error('Stripe is not configured. Please update src/config/stripe.ts with your Stripe keys.');
    showAlert('Payment system is not configured. Please contact support.', 'Configuration Error', '⚙️');
    return;
  }

  // Only basic and pro can be purchased via Stripe
  if (targetTier !== 'basic' && targetTier !== 'pro') {
    showAlert('This plan cannot be purchased online. Please contact us.', 'Contact Us', '📧');
    return;
  }

  // Start Stripe checkout
  await initiateStripeCheckout(targetTier as StripeTier, billingCycle);
}

/**
 * Initiate Stripe Checkout for a tier upgrade
 */
async function initiateStripeCheckout(tier: StripeTier, cycle: BillingCycle) {
  // Show loading state
  showLoadingOverlay(true, 'Preparing checkout...');

  try {
    // Call Firebase Function to create checkout session
    const createCheckoutSession = httpsCallable<
      { origin: string; tier: string; billingCycle: string },
      { sessionId: string }
    >(functions, 'createCheckoutSession');

    const result = await createCheckoutSession({
      origin: window.location.origin,
      tier: tier,
      billingCycle: cycle,
    });

    const { sessionId } = result.data;

    // Load Stripe.js dynamically
    const stripe = await loadStripe();

    if (!stripe) {
      throw new Error('Failed to load Stripe. Please try again.');
    }

    // Redirect to Stripe Checkout
    const { error } = await stripe.redirectToCheckout({ sessionId });

    if (error) {
      throw new Error(error.message);
    }

  } catch (error: any) {
    console.error('❌ Checkout failed:', error);

    // Handle specific Firebase function errors
    if (error.code === 'functions/unauthenticated') {
      showAlert('Please sign in to upgrade your plan.', 'Sign In Required', '🔐');
      window.location.href = '/dashboard/auth.html?redirect=pricing';
      return;
    }

    if (error.code === 'functions/not-found') {
      showAlert('The checkout service is not available. Please ensure Firebase Functions are deployed.', 'Service Unavailable', '🔌');
      return;
    }

    showAlert(`Checkout failed: ${error.message || 'Please try again.'}`, 'Checkout Failed', '❌');
  } finally {
    showLoadingOverlay(false);
  }
}

/**
 * Dynamically load Stripe.js
 * We load it dynamically to avoid bundling and ensure PCI compliance
 */
let stripePromise: Promise<any> | null = null;

async function loadStripe(): Promise<any> {
  if (stripePromise) {
    return stripePromise;
  }

  stripePromise = new Promise((resolve, reject) => {
    // Check if already loaded
    if ((window as any).Stripe) {
      resolve((window as any).Stripe(STRIPE_PUBLISHABLE_KEY));
      return;
    }

    // Load Stripe.js script
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/';
    script.async = true;

    script.onload = () => {
      if ((window as any).Stripe) {
        resolve((window as any).Stripe(STRIPE_PUBLISHABLE_KEY));
      } else {
        reject(new Error('Stripe.js failed to initialize'));
      }
    };

    script.onerror = () => {
      reject(new Error('Failed to load Stripe.js'));
    };

    document.head.appendChild(script);
  });

  return stripePromise;
}

/**
 * Show/hide loading overlay with custom message
 */
function showLoadingOverlay(show: boolean, message?: string) {
  if (!loadingOverlay) return;

  if (show) {
    loadingOverlay.classList.remove('hidden');
    const msgEl = loadingOverlay.querySelector('.loading-message');
    if (msgEl && message) {
      msgEl.textContent = message;
    }
  } else {
    loadingOverlay.classList.add('hidden');
  }
}

/**
 * Render pricing cards
 */
function renderPricingCards() {
  if (!pricingGrid) return;

  const cardsHTML = DISPLAY_TIERS.map((tier) => {
    const config = TIER_CONFIG[tier];
    const priceInfo = formatPrice(tier);
    const ctaConfig = getCTAConfig(tier);
    const isCurrentPlan = userProfile?.tier === tier;
    const isHighlighted = config.highlighted && !isCurrentPlan;

    // Build feature list HTML (show key features, not already shown as limits)
    const featureListHTML = config.features
      .filter(f => f !== 'uploads' && f !== 'analytics' && f !== 'ai_generation') // These are shown separately
      .slice(0, 4) // Show max 4 premium features
      .map(f => {
        const featureInfo = FEATURE_DISPLAY_NAMES[f];
        return `<li>${featureInfo.name}</li>`;
      })
      .join('');

    // Build storage limit text
    const storageLimit = config.storageLimit === -1
      ? 'Unlimited storage'
      : config.storageLimit === 0
        ? null // Don't show if no cloud storage
        : `${config.storageLimit / (1024 * 1024 * 1024)}GB cloud storage`;

    // Build bandwidth limit text
    const bandwidthLimit = config.bandwidthLimit === -1
      ? 'Unlimited bandwidth'
      : config.bandwidthLimit === 0
        ? null // Don't show if no hosted content
        : `${formatBandwidth(config.bandwidthLimit)}/mo bandwidth`;

    // Build video upload limit text
    const videoMinutesLimit = config.muxMinutesLimit === -1
      ? 'Unlimited video uploads'
      : config.muxMinutesLimit === 0
        ? null // Don't show if no uploads allowed
        : `${formatMuxMinutes(config.muxMinutesLimit)} video uploads`;

    // Build AI quota text
    let aiQuotaLimit: string | null = null;
    if (config.aiProLimit === -1 || config.aiProLimit >= 9999) {
      aiQuotaLimit = 'Unlimited AI generations';
    } else if (config.aiProLimit > 0 || config.aiFlashLimit > 0) {
      const parts: string[] = [];
      if (config.aiProLimit > 0) parts.push(`${config.aiProLimit} Pro`);
      if (config.aiFlashLimit > 0) parts.push(`${config.aiFlashLimit} Flash`);
      aiQuotaLimit = `${parts.join(' + ')} AI gens/mo`;
    }

    return `
      <div class="pricing-card ${isHighlighted ? 'highlighted' : ''} ${isCurrentPlan ? 'current' : ''}" data-tier="${tier}">
        <h3 class="tier-name">${config.displayName}</h3>
        <p class="tier-target">${config.targetAudience}</p>

        <div class="tier-price">
          ${priceInfo.isCustom
            ? `<span class="price-custom">${priceInfo.amount}</span>`
            : `<span class="price-currency">$</span><span class="price-amount">${priceInfo.amount}</span><span class="price-period">${priceInfo.period}</span>`
          }
        </div>

        <p class="tier-description">${config.description}</p>

        <ul class="feature-list">
          ${storageLimit ? `<li>${storageLimit}</li>` : '<li>Self-hosted only</li>'}
          ${bandwidthLimit ? `<li>${bandwidthLimit}</li>` : ''}
          ${videoMinutesLimit ? `<li>${videoMinutesLimit}</li>` : ''}
          ${aiQuotaLimit ? `<li>${aiQuotaLimit}</li>` : ''}
          ${featureListHTML}
        </ul>

        <button
          class="tier-cta ${ctaConfig.class}"
          ${ctaConfig.disabled ? 'disabled' : ''}
          data-tier="${tier}"
        >
          ${ctaConfig.text}
        </button>
      </div>
    `;
  }).join('');

  pricingGrid.innerHTML = cardsHTML;

  // Add click handlers to CTA buttons
  pricingGrid.querySelectorAll('.tier-cta').forEach((btn) => {
    const tier = btn.getAttribute('data-tier') as UserTier;
    const ctaConfig = getCTAConfig(tier);
    btn.addEventListener('click', ctaConfig.action);
  });
}

/**
 * Render comparison table
 */
function renderComparisonTable() {
  if (!comparisonTable) return;

  // Build header row
  const headerCells = DISPLAY_TIERS.map((tier) => {
    const config = TIER_CONFIG[tier];
    const isCurrentPlan = userProfile?.tier === tier;
    return `<th${isCurrentPlan ? ' style="background: rgba(52, 199, 89, 0.1);"' : ''}>${config.displayName}</th>`;
  }).join('');

  // Build body rows
  const bodyRows = ALL_FEATURES.map((feature) => {
    const cells = DISPLAY_TIERS.map((tier) => {
      const value = getFeatureValue(tier, feature.key);
      const isCurrentPlan = userProfile?.tier === tier;
      const bgStyle = isCurrentPlan ? ' style="background: rgba(52, 199, 89, 0.05);"' : '';

      if (typeof value === 'boolean') {
        return value
          ? `<td${bgStyle}><span class="check-icon">✓</span></td>`
          : `<td${bgStyle}><span class="cross-icon">—</span></td>`;
      }
      return `<td${bgStyle}>${value}</td>`;
    }).join('');

    return `<tr><td>${feature.name}</td>${cells}</tr>`;
  }).join('');

  comparisonTable.innerHTML = `
    <thead>
      <tr>
        <th>Feature</th>
        ${headerCells}
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
    </tbody>
  `;
}

// setBadgeTier and updateProBadge moved to shared UserDropdown.ts

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', init);
